import React, { useState, useEffect, useRef } from "react";
import { LottieLoadingIndicator } from "src/components";
import styled from "styled-components";

const VideoWrapper = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: black;
    height: 100%;
    min-height: 0;
`;


const CarouselWrapper = styled.div`
    position: relative;
    width: 100%;
    max-width: 800px;
    height: 100%;
    margin: 0 auto;
`;

const MediaContainer = styled.div`
    position: relative;
    overflow: hidden;
    border-radius: 12px;
    height: 100%;
    min-height: 0;
`;

const StyledImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
`;

const StyledVideo = styled.video`
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center center;
    overflow: visible;

`;

const Placeholder = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 140px;
    width: 100%;
    background-color: #f5f5f5;
    color: #888;
    font-size: 14px;
    font-style: italic;
    border: 1px dashed #ccc;
    padding: 10px;
    text-align: center;
    gap: 10px;
`;

const DotsContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 8px;
    padding: 0.7rem 0 0.25rem;
`;

const Dot = styled.div`
    height: 7px;
    width: 7px;
    border-radius: 50%;
    cursor: pointer;
    background: ${(props) => (props.active ? "black" : "gray")};
`;


const CarouselTableView = ({ items, autoPlay = true, autoPlayTime = 3000, noticeUuid, handleCarouselIndexChange }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const videoRef = useRef(null);

    useEffect(() => {
        if (!autoPlay) return;
        const interval = setInterval(() => {
            goToNext();
        }, autoPlayTime);
        return () => clearInterval(interval);
    }, [currentIndex, autoPlay, autoPlayTime]);

    const currentItem = items[currentIndex];

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
    }, [currentIndex, currentItem.videoUrl]);

    const goToPrev = () => {
        const newIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
        setCurrentIndex(newIndex);
        handleCarouselIndexChange(noticeUuid, newIndex + 1);
    };

    const goToNext = () => {
        const newIndex = currentIndex === items.length - 1 ? 0 : currentIndex + 1;
        setCurrentIndex(newIndex);
        handleCarouselIndexChange(noticeUuid, newIndex + 1);
    };

    return (
        <CarouselWrapper>
            {/* Media */}
            <MediaContainer>
                {currentItem.mediaType === "image" ? (
                    <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
                        <StyledImage src={currentItem.imageUrl} alt="carousel-item" />
                    </div>
                ) : currentItem.pendingMedia ? (
                    <Placeholder>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ whiteSpace: "nowrap" }}>Processing video...</span>
                            <LottieLoadingIndicator width="30px" height="30px" style={{ marginTop: "-22px" }} />
                        </div>
                    </Placeholder>
                ) : (
                    <VideoWrapper>
                        <StyledVideo
                            key={`video-${currentIndex}-${currentItem.videoUrl}`}
                            ref={videoRef}
                            controls
                            preload="metadata"
                        >
                            <source src={currentItem.videoUrl} type="video/mp4" />Your browser does not support the video tag.
                        </StyledVideo>
                    </VideoWrapper>
                )}
            </MediaContainer>

            <button
                onClick={goToPrev}
                disabled={currentIndex === 0}
                style={{
                    display: currentIndex === 0 ? "none" : "flex",
                    position: "absolute",
                    top: "50%",
                    left: "10px",
                    transform: "translateY(-50%)",
                    background: "rgba(255, 255, 255, 0.4)",
                    color: "white",
                    border: "none",
                    borderRadius: "50%",
                    width: "30px",
                    height: "30px",
                    cursor: "pointer",
                    justifyContent: "center",
                    alignItems: "center",
                    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
                }}
            >
                <svg
                    width="130px"
                    height="130px"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    stroke="#ffffff"
                    opacity={1}
                >
                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                    <g id="SVGRepo_iconCarrier">
                        <path
                            d="M15 20L7 12L15 4"
                            stroke="#FFFFFF"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        ></path>
                    </g>
                </svg>
            </button>
            <button
                onClick={goToNext}
                disabled={currentIndex === items.length - 1}
                style={{
                    display: currentIndex === items.length - 1 ? "none" : "flex",
                    position: "absolute",
                    top: "50%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    background: "rgba(255, 255, 255, 0.4)",
                    color: "white",
                    border: "none",
                    borderRadius: "50%",
                    width: "30px",
                    height: "30px",
                    cursor: "pointer",
                    justifyContent: "center",
                    alignItems: "center",
                    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
                }}
            >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                    <g
                        id="SVGRepo_tracerCarrier"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                        <path
                            d="M9 20L17 12L9 4"
                            stroke="#FFFFFF"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        ></path>
                    </g>
                </svg>
            </button>
            {/* Dots */}
            <DotsContainer>
                {items.map((_, index) => (
                    <Dot
                        key={index}
                        active={index === currentIndex}
                        onClick={() => setCurrentIndex(index)}
                    />
                ))}
            </DotsContainer>
        </CarouselWrapper>
    );
};

export default CarouselTableView;
